import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tradeAPI } from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  ListSubheader,
  Divider,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ViewList,
  AccountTree,
  Check as CheckIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';
import { apiRequest } from '../utils/api';
import StockTradeChart from '../components/StockTradeChart';
import { getClosedTradeNetPL } from '../utils/tradeUtils';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';
import { usePaperMode } from '../contexts/PaperModeContext';

// --- Helper Functions (Moved outside component for stability) ---

// Parse option symbol to extract components
const parseOptionSymbol = (symbol) => {
  if (!symbol) return { isOption: false, underlying: symbol };

  // Handle different option symbol formats
  // Descriptive formats from broker exports:
  // - FDX 12/19/25 250 PUT
  // - TSLA 01/09/2026 430.00 CALL
  // Support both 2-digit and 4-digit year and CALL/PUT words or C/P letters.
  const descWordMatchYY = symbol.match(/^([A-Z]+)\s+(\d{2})\/(\d{2})\/(\d{2})\s+(\d+(?:\.\d+)?)\s+(CALL|PUT)$/i);
  if (descWordMatchYY) {
    const [, underlying, mm, dd, yy, strikeStr, callPut] = descWordMatchYY;
    const expiration = `${yy}${mm}${dd}`; // YYMMDD
    const strike = parseFloat(strikeStr);
    const type = /call/i.test(callPut) ? 'C' : 'P';
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  const descWordMatchYYYY = symbol.match(/^([A-Z]+)\s+(\d{2})\/(\d{2})\/(\d{4})\s+(\d+(?:\.\d+)?)\s+(CALL|PUT)$/i);
  if (descWordMatchYYYY) {
    const [, underlying, mm, dd, yyyy, strikeStr, callPut] = descWordMatchYYYY;
    const yy = String(parseInt(yyyy, 10) % 100).padStart(2, '0');
    const expiration = `${yy}${mm}${dd}`; // YYMMDD
    const strike = parseFloat(strikeStr);
    const type = /call/i.test(callPut) ? 'C' : 'P';
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  // Descriptive with C/P letter suffix (older pattern): TSLA 01/09/2026 430.00 P
  const descLetterMatch = symbol.match(/^([A-Z]+)\s+(\d{2})\/(\d{2})\/(\d{4})\s+(\d+(?:\.\d+)?)\s+([CP])$/);
  if (descLetterMatch) {
    const [, underlying, mm, dd, yyyy, strikeStr, type] = descLetterMatch;
    const yy = String(parseInt(yyyy, 10) % 100).padStart(2, '0');
    const expiration = `${yy}${mm}${dd}`; // YYMMDD
    const strike = parseFloat(strikeStr);
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  
  // Schwab numeric format: AAPL  240119C00150000 (spaces between underlying and option details)
  const schwabMatch = symbol.match(/^([A-Z]+)\s+(\d{6})([CP])(\d{8})$/);
  if (schwabMatch) {
    const [, underlying, expiration, type, strikeStr] = schwabMatch;
    const strike = parseInt(strikeStr) / 1000; // Convert from 8-digit format
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  
  // Standard format: AAPL240119C00150000
  const standardMatch = symbol.match(/^([A-Z]+)(\d{6})([CP])(\d{8})$/);
  if (standardMatch) {
    const [, underlying, expiration, type, strikeStr] = standardMatch;
    const strike = parseInt(strikeStr) / 1000;
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  
  // Tradier format: AAPL240119C150 (shorter strike format)
  const tradierMatch = symbol.match(/^([A-Z]+)(\d{6})([CP])(\d+(?:\.\d+)?)$/);
  if (tradierMatch) {
    const [, underlying, expiration, type, strikeStr] = tradierMatch;
    const strike = parseFloat(strikeStr);
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }
  
  // Robinhood verbose format: TSLL 01/16/26 $20.5 Call
  // Also supports formats without $ and case insensitive
  const verboseMatch = symbol.match(/^([A-Z]+) (\d{2})\/(\d{2})\/(\d{2}) \$?([\d.]+) (Call|Put)$/i);
  if (verboseMatch) {
    const [, underlying, month, day, year, strikeStr, typeStr] = verboseMatch;
    const expiration = `${year}${month}${day}`;
    const type = typeStr.toUpperCase() === 'CALL' || typeStr.toUpperCase() === 'C' ? 'C' : 'P';
    const strike = parseFloat(strikeStr);
    return {
      isOption: true,
      underlying,
      expiration,
      type,
      strike,
      originalSymbol: symbol
    };
  }

  // Not an option symbol
  return { isOption: false, underlying: symbol, originalSymbol: symbol };
};

// Format a date string (e.g. 'YYYY-MM-DD' or ISO) as MM/DD/YY without
// timezone shifting that would otherwise roll a date back a day.
const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const s = String(dateStr);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1].slice(2)}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
};

// Format individual option display label
const formatOptionLabel = (trade) => {
  const optionInfo = parseOptionSymbol(trade.originalSymbol || trade.symbol);
  
  if (!optionInfo.isOption) {
    return trade.originalSymbol || trade.symbol; // Return as-is for stocks
  }
  
  const { underlying, expiration, type, strike } = optionInfo;
  
  // Format expiration from YYMMDD to MM/DD/YY
  let formattedExpiration = expiration;
  if (expiration && expiration.length === 6) {
    const year = expiration.substring(0, 2);
    const month = parseInt(expiration.substring(2, 4), 10);
    const day = parseInt(expiration.substring(4, 6), 10);
    formattedExpiration = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  }
  
  // Format strike price (remove unnecessary decimals)
  const formattedStrike = strike && typeof strike === 'number' ? 
    (strike % 1 === 0 ? strike.toString() : strike.toFixed(2)) : 'N/A';
  
  // Format option type
  const optionType = type === 'C' ? 'CALL' : 'PUT';
  
  return `${underlying} ${formattedExpiration} ${formattedStrike} ${optionType}`;
};

// Determine trade status (open/closed)
const getTradeStatus = (trade) => {
  const tx = String(trade.transactionType || trade.transaction_type || '').toUpperCase();
  const tt = String(trade.tradeType || trade.trade_type || '').toUpperCase();
  const pe = String(trade.positionEffect || trade.position_effect || '').toUpperCase();

  // Explicit closing events
  if (tx === 'OEXP' || tx === 'OASGN' || tx === 'DIVIDEND') return 'Closed';
  if (tt === 'BTC' || tt === 'STC' || tt === 'DIVIDEND' || tt === 'DIV') return 'Closed';
  if (pe.includes('BUY TO CLOSE') || pe.includes('SELL TO CLOSE')) return 'Closed';

  // Realized P/L or Dividend present indicates a closed row
  const realized = parseFloat(trade.realizedPL || trade.realized_pl || trade.netPL || trade.net_pl || '');
  const div = parseFloat(trade.dividend || trade.dividend_amount || '');
  if (!isNaN(realized) && Math.abs(realized) > 0) return 'Closed';
  if (!isNaN(div) && Math.abs(div) > 0) return 'Closed';

  // Options: if past expiration, treat as closed (do not show expired opens as open)
  const optionInfo = parseOptionSymbol(trade.originalSymbol || trade.symbol);
  if (optionInfo.isOption && optionInfo.expiration) {
    const expYear = 2000 + parseInt(optionInfo.expiration.substring(0, 2));
    const expMonth = parseInt(optionInfo.expiration.substring(2, 4)) - 1;
    const expDay = parseInt(optionInfo.expiration.substring(4, 6));
    const expirationDate = new Date(expYear, expMonth, expDay);
    expirationDate.setHours(23, 59, 59, 999);
    if (expirationDate < new Date()) return 'Closed';
  }

  return 'Open';
};

// Client-side reconciliation to compute realized P/L on close rows and hide fully matched opening legs
const reconcileTradesForDisplay = (trades) => {
  // Return trades as-is from DB without any reconciliation
  // This ensures DB values are displayed directly
  const rows = [...(trades || [])].map(r => ({ ...r, _hideOpeningLeg: false }));

  // Debug: Log all trades
  console.log('DEBUG: All trades in reconcileTradesForDisplay:', rows.map(r => ({ id: r.id, symbol: r.symbol, realizedPL: r.realizedPL, tradeType: r.tradeType })));

  return rows;
};

// Helper: Determine trade side (Long/Short)
const getTradeSide = (p) => {
  if (p._closedSide) return p._closedSide;
  
  // Explicit effects
  const e = String(p.positionEffect || '').toUpperCase();
  if (e.includes('BUY')) return 'LONG';
  if (e.includes('SELL')) return 'SHORT';
  
  // Trade Type
  const tt = String(p.tradeType || '').toUpperCase();
  if (tt === 'BUY' || tt === 'BTO' || tt === 'BUY_TO_OPEN') return 'LONG';
  if (tt === 'SELL' || tt === 'STO' || tt === 'SELL_TO_OPEN') return 'SHORT';
  
  // Fallback to quantity
  // For rows that lack explicit effect (e.g. some imports), assume Qty > 0 is Long, Qty < 0 is Short
  const qty = parseFloat(p.quantity);
  if (qty > 0) return 'LONG';
  if (qty < 0) return 'SHORT';
  
  return 'UNKNOWN';
};

// Helper: Check if two positions form a valid vertical spread
const isVerticalSpread = (p1, p2) => {
  if (!p1.optionInfo || !p2.optionInfo) return false;
  // Must be same option type (Put/Put or Call/Call)
  if (p1.optionInfo.type !== p2.optionInfo.type) return false;
  
  // Determine Side (Long/Short)
  const s1 = getTradeSide(p1);
  const s2 = getTradeSide(p2);
  
  // Must be opposite sides (Long vs Short)
  if (s1 === s2 || s1 === 'UNKNOWN' || s2 === 'UNKNOWN') return false;
  
  // Must have same absolute quantity (Strict matching for detection)
  if (Math.abs(p1.quantity) !== Math.abs(p2.quantity)) return false;
  // Must have same trade date (User preference for grouping)
  // Normalizing dates to strings for comparison
  const d1 = p1.tradeDate ? new Date(p1.tradeDate).toISOString().split('T')[0] : 'null';
  const d2 = p2.tradeDate ? new Date(p2.tradeDate).toISOString().split('T')[0] : 'null';
  if (d1 !== d2) return false;
  
  return true;
};

const getStrategyName = (legs) => {
    // Assumes 2 legs for vertical
    const sorted = [...legs].sort((a,b) => a.optionInfo.strike - b.optionInfo.strike);
    const [lower, higher] = sorted;
    const type = lower.optionInfo.type;
    
    // Determine sides
    const lowerSide = getTradeSide(lower);
    const higherSide = getTradeSide(higher);

    if (type === 'C') {
        // Call Debit: Long Lower, Short Higher
        if (lowerSide === 'LONG' && higherSide === 'SHORT') return 'Call Debit Spread';
        // Call Credit: Short Lower, Long Higher
        if (lowerSide === 'SHORT' && higherSide === 'LONG') return 'Call Credit Spread';
    } else {
        // Put Debit: Short Lower, Long Higher (Bear Put)
        if (lowerSide === 'SHORT' && higherSide === 'LONG') return 'Put Debit Spread';
        // Put Credit: Long Lower, Short Higher (Bull Put)
        if (lowerSide === 'LONG' && higherSide === 'SHORT') return 'Put Credit Spread';
    }
    return 'Vertical Spread';
};

const formatSpreadLabel = (group) => {
  if (!group || !group.legs) return group.spreadType;
  const { expiration, spreadType, legs } = group;
  
  let formattedExpiration = expiration;
  if (expiration && expiration.length === 6) {
    formattedExpiration = `${expiration.substring(2, 4)}/${expiration.substring(4, 6)}/${expiration.substring(0, 2)}`;
  }
  
  const strikes = legs.map(leg => leg.optionInfo?.strike).filter(s => s).sort((a, b) => a - b);
  if (strikes.length >= 2) {
    const strikeStr = strikes.join('/');
    let spreadAbbr = spreadType
      .replace('Put Credit Spread', 'PCS')
      .replace('Put Debit Spread', 'PDS')
      .replace('Call Credit Spread', 'CCS')
      .replace('Call Debit Spread', 'CDS')
      .replace('Iron Condor', 'IC')
      .replace(' Spread', '');
      
    return `${formattedExpiration} ${strikeStr} ${spreadAbbr}`;
  }
  return spreadType;
};

// Helper: map period keys to absolute start/end dates
const getRangeForPeriod = (p) => {
  const today = new Date();
  const endIso = today.toISOString().split('T')[0];
  const lower = (days) => {
    const d = new Date(today);
    d.setDate(today.getDate() - days);
    return d.toISOString().split('T')[0];
  };
  switch (p) {
    case '30d': return { startDate: lower(30), endDate: endIso };
    case '60d': return { startDate: lower(60), endDate: endIso };
    case '90d': return { startDate: lower(90), endDate: endIso };
    case '180d': return { startDate: lower(180), endDate: endIso };
    case 'current_month': {
      const y = today.getFullYear();
      const m = today.getMonth() + 1;
      const startIso = `${y}-${String(m).padStart(2, '0')}-01`;
      return { startDate: startIso, endDate: endIso };
    }
    case 'previous_month': {
      const y = today.getFullYear();
      const m = today.getMonth();
      const prevY = m === 0 ? y - 1 : y;
      const prevM = m === 0 ? 12 : m;
      const startIso = `${prevY}-${String(prevM).padStart(2, '0')}-01`;
      const lastDay = new Date(prevY, prevM, 0).getDate();
      const end = `${prevY}-${String(prevM).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return { startDate: startIso, endDate: end };
    }
    case 'ytd': {
      const y = today.getFullYear();
      const startIso = `${y}-01-01`;
      return { startDate: startIso, endDate: endIso };
    }
    case 'last_year': {
      const y = today.getFullYear() - 1;
      const startIso = `${y}-01-01`;
      const end = `${y}-12-31`;
      return { startDate: startIso, endDate: end };
    }
    default:
      return null;
  }
};

// --- END HELPERS ---

const StockAnalysis = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [stockList, setStockList] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockData, setStockData] = useState(null);
  const { paperMode } = usePaperMode();
  const modeKey = (name) => `${paperMode ? 'paper_' : ''}${name}`;
  const modeGet = (name) => localStorage.getItem(modeKey(name)) ?? localStorage.getItem(name);
  const [timePeriod, setTimePeriod] = useState(() => modeGet('global_time_period') || '30d');
  const [customStartDate, setCustomStartDate] = useState(() => modeGet('global_custom_start') || '');
  const [customEndDate, setCustomEndDate] = useState(() => modeGet('global_custom_end') || '');
  const [tradesByPeriod, setTradesByPeriod] = useState([]);
  const [positions, setPositions] = useState([]);
  const [individualTrades, setIndividualTrades] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(['all']);
  // Draft state for deferred filtering (verified defined - fixes ESLint false positive - touched)
  const [draftAccount, setDraftAccount] = useState(['all']);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [tradesSort, setTradesSort] = useState({ field: 'tradeDate', direction: 'desc' });
  const [groupByOrderId, setGroupByOrderId] = useState(false);
  const [recentSymbols, setRecentSymbols] = useState([]);
  const [quotesBySymbol, setQuotesBySymbol] = useState({});
  const [closedTradesView, setClosedTradesView] = useState('strategy'); // 'individual', 'order', 'strategy'
  const [analysisData, setAnalysisData] = useState(null);
  const [leftTab, setLeftTab] = useState(0);

  // Cache helper functions for open positions (shared with StockSummary)
  const getOpenPositionsCacheKey = () => {
    // NOTE: bump the version suffix whenever the position-sourcing logic changes so
    // stale caches (e.g. phantom DB positions for API accounts) are invalidated.
    return `openpositions_v4_${selectedAccount.join('_')}`;
  };

  const getCachedOpenPositions = () => {
    try {
      const key = getOpenPositionsCacheKey();
      console.log('StockAnalysis: Open positions cache key:', key);
      const cached = localStorage.getItem(key);
      console.log('StockAnalysis: Cached data found:', !!cached);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const cacheAge = Date.now() - timestamp;
        console.log('StockAnalysis: Cache age:', cacheAge, 'ms, data length:', data?.length);
        // Use cache if less than 24 hours old
        if (cacheAge < 24 * 60 * 60 * 1000) {
          console.log('StockAnalysis: Using cached data');
          return data;
        } else {
          console.log('StockAnalysis: Cache expired, age:', cacheAge);
        }
      }
    } catch (e) {
      console.error('Error reading open positions cache:', e);
    }
    console.log('StockAnalysis: No cache available');
    return null;
  };

  const setCachedOpenPositions = (data) => {
    try {
      const key = getOpenPositionsCacheKey();
      console.log('StockAnalysis: Saving to cache, key:', key, 'data length:', data?.length);
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      console.log('StockAnalysis: Cache saved successfully');
    } catch (e) {
      console.error('Error writing open positions cache:', e);
    }
  };
  
  // Spread detection state
  const [groupedOpenTrades, setGroupedOpenTrades] = useState([]);
  const [groupedClosedTrades, setGroupedClosedTrades] = useState([]);
  const [expandedSpreads, setExpandedSpreads] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Assignment Dialog State
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTradeForAssign, setSelectedTradeForAssign] = useState(null);
  const [assignQuantity, setAssignQuantity] = useState('');
  const [error, setError] = useState(null);

  // Grouped Accounts for dropdown
  const taxableAccounts = accounts.filter(a => a.accountType === 'taxable');
  const nonTaxableAccounts = accounts.filter(a => a.accountType === 'non-taxable');

  const handleAccountCategorySelect = (category) => {
    const categoryAccounts = category === 'taxable' ? taxableAccounts : nonTaxableAccounts;
    const categoryIds = categoryAccounts.map(a => a.id);
    
    // Check if all accounts in this category are already selected
    const allSelected = categoryIds.every(id => draftAccount.includes(id));
    
    let newDraft;
    if (allSelected) {
      // Unselect all in category
      newDraft = draftAccount.filter(id => !categoryIds.includes(id));
    } else {
      // Select all in category
      newDraft = Array.from(new Set([...draftAccount, ...categoryIds])).filter(id => id !== 'all');
    }
    
    if (newDraft.length === 0) newDraft = ['all'];
    setDraftAccount(newDraft);
  };

  // Fetch stock performance data
  const fetchStockData = useCallback(async () => {
    if (!selectedStock) return;

    setLoading(true);
    try {
      // Build query parameters
      // const accountParam = selectedAccount !== 'all' ? `&accountId=${selectedAccount}` : '';
      
      // Build date range params
      const baseParams = new URLSearchParams();
      if (timePeriod === 'custom' && customStartDate && customEndDate) {
        baseParams.append('startDate', customStartDate);
        baseParams.append('endDate', customEndDate);
      } else {
        const range = getRangeForPeriod(timePeriod);
        if (range) {
          baseParams.append('startDate', range.startDate);
          baseParams.append('endDate', range.endDate);
        }
      }
      if (selectedAccount.length > 0 && !selectedAccount.includes('all')) {
        baseParams.append('accountId', selectedAccount.join(','));
        // If filtering by specific account, include open trades to ensure visibility
        baseParams.append('includeOpen', 'true');
      }

      const perfUrl = `/api/stocks/${selectedStock.symbol}/performance${baseParams.toString() ? `?${baseParams.toString()}` : ''}`;
      const posUrl = `/api/stocks/${selectedStock.symbol}/positions${baseParams.toString() ? `?${baseParams.toString()}` : ''}`;

      const tradesParams = new URLSearchParams(baseParams);
      tradesParams.append('symbol', selectedStock.symbol);
      const tradesUrl = `/api/trades?${tradesParams.toString()}`;

      const [performanceResponse, positionsResponse, tradesResponse] = await Promise.all([
        apiRequest(perfUrl),
        apiRequest(posUrl),
        apiRequest(tradesUrl)
      ]);
      
      setStockData(performanceResponse.data.performance || null);
      setTradesByPeriod(performanceResponse.data.tradesByPeriod || []);
      setPositions(positionsResponse.data || []);
      setIndividualTrades(tradesResponse.data.trades || tradesResponse.data || []);
    } catch (error) {
      console.error('Error fetching stock data:', error);
      setStockData(null);
      setTradesByPeriod([]);
      setPositions([]);
      setIndividualTrades([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStock, timePeriod, customStartDate, customEndDate, selectedAccount]);

  const handleOpenAssignDialog = (trade) => {
    setSelectedTradeForAssign(trade);
    setAssignQuantity(Math.abs(trade.quantity));
    setAssignDialogOpen(true);
  };

  const handleSubmitAssign = async () => {
    if (!selectedTradeForAssign || !assignQuantity) return;
    setError(null);
    try {
        await apiRequest(`/api/trades/${selectedTradeForAssign.id}/assign`, {
            method: 'POST',
            body: JSON.stringify({ quantity: parseFloat(assignQuantity) })
        });
        setAssignDialogOpen(false);
        // Refresh trades
        fetchStockData();
    } catch (e) {
        console.error('Failed to assign trade', e);
        const detail = e.response?.data?.details || e.response?.data?.error || e.message;
        setError(`Failed to mark trade as assigned: ${detail}`);
    }
  };

  // Restore the incoming mode's saved period on live <-> paper toggle.
  // Declared before the persist effects so it reads the new mode's keys first.
  const prevPaperModeRef = React.useRef(paperMode);
  useEffect(() => {
    if (prevPaperModeRef.current === paperMode) return;
    prevPaperModeRef.current = paperMode;
    setTimePeriod(modeGet('global_time_period') || '30d');
    setCustomStartDate(modeGet('global_custom_start') || '');
    setCustomEndDate(modeGet('global_custom_end') || '');
  }, [paperMode]);

  // Persist time period selection
  useEffect(() => {
    try { localStorage.setItem(modeKey('global_time_period'), timePeriod); } catch {}
  }, [timePeriod, paperMode]);

  // Persist custom date range
  useEffect(() => {
    try {
      localStorage.setItem(modeKey('global_custom_start'), customStartDate || '');
      localStorage.setItem(modeKey('global_custom_end'), customEndDate || '');
    } catch {}
  }, [customStartDate, customEndDate, paperMode]);


  // Derive realized P/L for display (net of commissions/fees, includes dividends)
  const getRealizedPLForDisplay = (trade) => {
    const div = parseFloat(trade.dividend || trade.dividend_amount || 0);
    return getClosedTradeNetPL(trade) + div;
  };

  // Derive net P/L for display (same basis as realized: net_pl already nets fees)
  const getNetPLForDisplay = (trade) => getRealizedPLForDisplay(trade);

  // Calculate unrealized P/L for open trades/positions
  const calculateUnrealizedPL = (trade) => {
    if (!trade) return 0;
    // Broker positions (no transactionType) are always "open"; DB trades need status check
    const isBrokerPosition = !trade.transactionType && !trade.transaction_type && !trade.tradeType && !trade.trade_type;
    if (!isBrokerPosition && getTradeStatus(trade) !== 'Open') return 0;

    const quantity = parseFloat(trade.quantity) || 0;
    const costBasis = parseFloat(trade.cost ?? trade.cost_basis ?? trade.totalCost ?? 0) || 0;

    // 1. Use totalPL from broker API when available (most accurate)
    const totalPL = parseFloat(trade.totalPL ?? trade.total_pl ?? '');
    if (!isNaN(totalPL)) return totalPL;

    // 2. Use marketValue + cost when available (broker returns these)
    const marketValue = parseFloat(trade.marketValue ?? trade.market_value ?? 0) || 0;
    if (marketValue !== 0 || (trade.marketValue !== undefined && trade.marketValue !== null)) {
      const absCost = Math.abs(costBasis);
      if (quantity < 0) {
        // Short: Proceeds (Abs Cost) - Market Value
        return absCost - Math.abs(marketValue);
      }
      return marketValue - absCost;
    }

    // 3. Compute from live quotes
    const optionInfo = trade.optionInfo || parseOptionSymbol(trade.originalSymbol || trade.symbol);
    const isOption = !!optionInfo.isOption;
    const multiplier = isOption ? 100 : 1;
    let quoteSymbol = optionInfo.underlying;
    if (isOption && optionInfo.expiration && optionInfo.type && optionInfo.strike !== undefined) {
      const strikeText = Number.isInteger(optionInfo.strike) ? `${optionInfo.strike}` : `${optionInfo.strike}`;
      quoteSymbol = `${optionInfo.underlying}${optionInfo.expiration}${optionInfo.type}${strikeText}`;
    }
    const quote = quotesBySymbol[quoteSymbol];
    const lastPrice = quote ? (parseFloat(quote.last) || parseFloat(quote.close) || 0) : 0;
    const entryPrice = parseFloat(trade.price ?? trade.avgPrice ?? trade.averagePrice ?? 0) || 0;
    if (lastPrice > 0 || entryPrice > 0) {
      const unrealizedFromQuote = (lastPrice - entryPrice) * quantity * multiplier;
      if (!isNaN(unrealizedFromQuote)) return unrealizedFromQuote;
    }

    return parseFloat(trade.netPL ?? trade.net_pl ?? 0) || 0;
  };

  // Fetch stock list and accounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch stocks and accounts independently so one failure doesn't block the other
        let stocksResponse = null;
        let accountsResponse = null;
        try {
          stocksResponse = await apiRequest('/api/stocks');
        } catch (err) {
          console.error('Error fetching stocks:', err);
        }
        try {
          accountsResponse = await apiRequest('/api/accounts?include_inactive=true');
        } catch (err) {
          console.error('Error fetching accounts:', err);
        }
        setStockList((stocksResponse && stocksResponse.data) ? stocksResponse.data : []);
        setAccounts((accountsResponse && accountsResponse.data) ? accountsResponse.data : []);
        // Load recent symbols from localStorage
        try {
          const saved = localStorage.getItem('recent_symbols');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) setRecentSymbols(parsed);
          }
        } catch (e) {
          console.warn('Failed to load recent symbols:', e);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    fetchInitialData();
  }, []);

  // Set selected stock from URL parameter
  useEffect(() => {
    if (symbol && stockList.length > 0) {
      const stock = stockList.find(s => s.symbol === symbol);
      if (stock) {
        setSelectedStock(stock);
        // Update recent symbols
        try {
          const updated = [stock.symbol, ...recentSymbols.filter(s => s !== stock.symbol)].slice(0, 10);
          setRecentSymbols(updated);
          localStorage.setItem('recent_symbols', JSON.stringify(updated));
        } catch (e) {
          console.warn('Failed to update recent symbols from URL:', e);
        }
      }
    } else if (symbol && (!stockList || stockList.length === 0)) {
      // Fallback: allow navigation directly via URL even if stocks aren't loaded yet
      setSelectedStock({ symbol, name: symbol });
      try {
        const updated = [symbol, ...recentSymbols.filter(s => s !== symbol)].slice(0, 10);
        setRecentSymbols(updated);
        localStorage.setItem('recent_symbols', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to update recent symbols from URL fallback:', e);
      }
    }
  }, [symbol, stockList]);

  // Fetch open positions from trades (unmatched opening trades)
  useEffect(() => {
    const fetchOpenPositions = async () => {
      if (!selectedStock) {
        setOpenPositions([]);
        return;
      }

      const filterForSymbol = (list) => (list || []).filter(position => {
        const posSymbol = (position.symbol || '').toUpperCase();
        const selectedSymbol = selectedStock.symbol.toUpperCase();

        if (posSymbol === selectedSymbol) return true;

        // Check if it's an option for this stock
        if (position.optionInfo && position.optionInfo.underlying) {
          if (position.optionInfo.underlying.toUpperCase() === selectedSymbol) return true;
        }

        // Fallback parsing if optionInfo is missing
        const info = parseOptionSymbol(position.symbol);
        if (info.isOption && info.underlying && info.underlying.toUpperCase() === selectedSymbol) return true;

        // Handle cases where symbol might be "AAPL Jan 21 2022 150 Call" etc.
        if (posSymbol.startsWith(selectedSymbol)) return true;

        return false;
      });

      // Show cached positions instantly, but ALWAYS revalidate — a stale cache
      // (e.g. written before a broker outage was fixed) must not hide positions.
      const cached = getCachedOpenPositions();
      if (cached && cached.length > 0) {
        setOpenPositions(filterForSymbol(cached));
      }

      setPositionsLoading(true);
      try {
        // Build filters - use live=false to use Supabase/Sequelize instead of broker API
        const filters = { live: false };
        if (selectedAccount.length > 0 && !selectedAccount.includes('all')) {
          filters.accountId = selectedAccount.join(',');
        }

        // Fetch open positions from trades API
         const response = await tradeAPI.getOpenPositions(filters);
        const allOpenPositions = response.data.openPositions || [];

        // Save all positions to cache (only if we got data)
        if (allOpenPositions.length > 0) {
          setCachedOpenPositions(allOpenPositions);
        }

        setOpenPositions(filterForSymbol(allOpenPositions));
      } catch (error) {
        console.error('Error fetching open positions:', error);
        if (!(cached && cached.length > 0)) setOpenPositions([]);
      } finally {
        setPositionsLoading(false);
      }
    };

    fetchOpenPositions();
  }, [selectedStock, selectedAccount]);

  // Fetch fundamentals / analyst data / news for the selected symbol (Yahoo bundle)
  useEffect(() => {
    if (!selectedStock?.symbol) {
      setAnalysisData(null);
      return;
    }
    let cancelled = false;
    apiRequest(`/api/stocks/${encodeURIComponent(selectedStock.symbol)}/analysis`)
      .then(resp => { if (!cancelled) setAnalysisData(resp.data?.data || null); })
      .catch(() => { if (!cancelled) setAnalysisData(null); });
    return () => { cancelled = true; };
  }, [selectedStock]);

  // Fetch live quotes for the selected symbol's open trades/options
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        if (!selectedStock) return;
        // Use first selected account for quotes, or first available account if 'all'
        const accountId = (selectedAccount.length > 0 && !selectedAccount.includes('all')) 
          ? selectedAccount[0] 
          : (accounts[0]?.id || null);
        if (!accountId) return;

        // Build symbols set from open trades AND openPositions (broker positions)
        const openTrades = (individualTrades || []).filter(t => getTradeStatus(t) === 'Open');
        const symbolsSet = new Set();
        
        if (selectedStock && selectedStock.symbol) {
          symbolsSet.add(selectedStock.symbol);
        }

        const addOptionSymbol = (sym) => {
          const info = parseOptionSymbol(sym);
          if (info.isOption) {
            const strikeText = Number.isInteger(info.strike) ? `${info.strike}` : `${info.strike}`;
            symbolsSet.add(`${info.underlying}${info.expiration}${info.type}${strikeText}`);
          }
        };

        openTrades.forEach(trade => addOptionSymbol(trade.originalSymbol || trade.symbol));
        (openPositions || []).forEach(pos => addOptionSymbol(pos.symbol || pos.originalSymbol));
        const symbols = Array.from(symbolsSet);
        if (symbols.length === 0) return;

        const response = await apiRequest(`/api/accounts/${accountId}/quotes`, {
          method: 'POST',
          body: JSON.stringify({ symbols })
        });
        const quotesArray = (response && response.data && response.data.quotes) ? response.data.quotes : (response.data || []);
        const nextQuotes = {};
        quotesArray.forEach(q => {
          const sym = q?.symbol || q?.Symbol || q?.symbol_id || q?.id;
          if (sym) nextQuotes[sym] = q;
        });
        setQuotesBySymbol(nextQuotes);
      } catch (e) {
        console.warn('Failed to fetch quotes:', e);
      }
    };

    fetchQuotes();
  }, [selectedStock, selectedAccount, accounts, individualTrades, openPositions]);

  useEffect(() => {
    fetchStockData();
  }, [fetchStockData]);

  const handleStockChange = (event, newValue) => {
    let next = null;
    if (typeof newValue === 'string') {
      const sym = newValue.trim().toUpperCase();
      if (sym) next = { symbol: sym, name: sym };
    } else if (newValue && typeof newValue === 'object') {
      next = newValue;
    }

    setSelectedStock(next);

    if (next && next.symbol) {
      // Persist to recent symbols
      try {
        const updated = [next.symbol, ...recentSymbols.filter(s => s !== next.symbol)].slice(0, 10);
        setRecentSymbols(updated);
        localStorage.setItem('recent_symbols', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save recent symbols:', e);
      }
      navigate(`/stock-analysis/${next.symbol}`);
    } else {
      navigate('/stock-analysis');
    }
  };

  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value);
  };

  const handleAccountChange = (event) => {
    const {
      target: { value },
    } = event;
    
    const newValue = typeof value === 'string' ? value.split(',') : value;
    
    // If "all" is selected, clear other selections
    if (newValue.includes('all') && !draftAccount.includes('all')) {
      setDraftAccount(['all']);
      return;
    }
    
    // If "all" was selected and we add another, remove "all"
    if (draftAccount.includes('all') && newValue.length > 1) {
      setDraftAccount(newValue.filter(v => v !== 'all'));
      return;
    }
    
    // If nothing selected, default to "all"
    if (newValue.length === 0) {
      setDraftAccount(['all']);
      return;
    }
    
    setDraftAccount(newValue);
  };

  const applyAccountSelection = () => {
    setSelectedAccount(draftAccount);
  };

  const handleTradesSort = (field) => {
    const isAsc = tradesSort.field === field && tradesSort.direction === 'asc';
    setTradesSort({ field, direction: isAsc ? 'desc' : 'asc' });
  };

  // Sort and group individual trades (reconciled to single close rows)
  const processedIndividualTrades = useMemo(() => {
    let trades = reconcileTradesForDisplay(individualTrades);

    // Deduplicate trades by ID to prevent UI duplicates
    const seenIds = new Set();
    trades = trades.filter(trade => {
      if (trade.id && seenIds.has(trade.id)) {
        return false;
      }
      if (trade.id) {
        seenIds.add(trade.id);
      }
      return true;
    });

    if (groupByOrderId && closedTradesView === 'order') {
      // Group trades by order ID
      const groupedTrades = {};
      trades.forEach(trade => {
        const orderId = trade.orderId || 'no-order-id';
        if (!groupedTrades[orderId]) {
          groupedTrades[orderId] = [];
        }
        groupedTrades[orderId].push(trade);
      });
      
      // Flatten grouped trades with order ID headers
      trades = [];
      Object.entries(groupedTrades).forEach(([orderId, orderTrades]) => {
        // Sort trades within each order group
        const sortedOrderTrades = orderTrades.sort((a, b) => {
          const aDate = new Date(a.tradeDate || 0);
          const bDate = new Date(b.tradeDate || 0);
          return aDate - bDate;
        });
        
        // Add order header
        if (orderId !== 'no-order-id') {
          trades.push({
            isOrderHeader: true,
            orderId: orderId,
            tradeCount: orderTrades.length,
            totalPL: orderTrades.reduce((sum, t) => sum + (parseFloat(t.netPL) || 0), 0)
          });
        }
        
        trades.push(...sortedOrderTrades);
      });
    } else {
      // Regular sorting
      const { field, direction } = tradesSort;
      trades = trades.sort((a, b) => {
        let aValue = a[field];
        let bValue = b[field];

        // Handle different data types
        if (field === 'tradeDate') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (field === 'symbol') {
          aValue = (a.originalSymbol || a.symbol || '').toLowerCase();
          bValue = (b.originalSymbol || b.symbol || '').toLowerCase();
        } else if (field === 'netPL') {
          aValue = parseFloat(aValue || 0);
          bValue = parseFloat(bValue || 0);
        }

        if (aValue < bValue) {
          return direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return trades;
  }, [individualTrades, groupByOrderId, tradesSort, closedTradesView]);

  const calculateSpreadMetrics = (spread) => {
    if (!spread || !spread.legs) return {};
    let netDebit = 0;
    let totalMarketValue = 0;
    let totalUnrealizedPL = 0;
    let totalCost = 0;
    
    spread.legs.forEach(leg => {
      const legCost = parseFloat(leg.cost) || 0;
      // const legMarketValue = parseFloat(leg.marketValue) || 0; 
      const uPL = calculateUnrealizedPL(leg);
      
      totalUnrealizedPL += uPL;
      totalCost += Math.abs(legCost);
      
      // For Net Debit/Credit:
      // Cost (Debit) - Proceeds (Credit)
      const c = parseFloat(leg.cost) || 0;
      const p = parseFloat(leg.proceeds) || 0;
      netDebit += (c - p);
      
      // Estimate Market Value
      // MV = Cost + uPL (roughly)
      // Actually, let's look up current price if possible
      const info = leg.optionInfo;
      if (info) {
          const strikeText = Number.isInteger(info.strike) ? `${info.strike}` : `${info.strike}`;
          const quoteSymbol = `${info.underlying}${info.expiration}${info.type}${strikeText}`;
          const quote = quotesBySymbol[quoteSymbol];
          const price = quote ? (parseFloat(quote.last) || parseFloat(quote.close) || 0) : 0;
          totalMarketValue += (price * leg.quantity * 100);
      }
    });

    // BP Effect Calculation
    let bpEffect = 0;
    
    // Helper to get valid strike
    const getStrike = (leg) => {
        if (leg.optionInfo && typeof leg.optionInfo.strike === 'number') return leg.optionInfo.strike;
        return 0;
    };

    if (spread.legs.length === 2) {
        const strike1 = getStrike(spread.legs[0]);
        const strike2 = getStrike(spread.legs[1]);
        
        if (strike1 > 0 && strike2 > 0) {
             const qty = Math.abs(spread.legs[0].quantity);
             bpEffect = Math.abs(strike1 - strike2) * 100 * qty;
        }
    }
    
    return {
      totalCost: totalCost,
      netDebit, // Positive = Debit, Negative = Credit
      totalMarketValue,
      unrealizedPL: totalUnrealizedPL,
      isCredit: netDebit < 0,
      bpEffect
    };
  };

  const calculateClosedSpreadMetrics = (spread) => {
      if (!spread || !spread.legs) return {};
      let totalRealizedPL = 0;
      let totalCost = 0;
      let totalProceeds = 0;
      let totalFees = 0;

      spread.legs.forEach(leg => {
          totalRealizedPL += getClosedTradeNetPL(leg);
          totalCost += (parseFloat(leg.cost) || 0);
          totalProceeds += (parseFloat(leg.proceeds) || 0);
          totalFees += (parseFloat(leg.fees) || 0) + (parseFloat(leg.commission) || 0);
      });

      return {
          totalRealizedPL,
          totalCost,
          totalProceeds,
          totalFees,
          netPL: totalRealizedPL // Net PL is usually same as Realized PL if fees already deducted
      };
  };

  const toggleSpreadExpansion = (id) => {
    const next = new Set(expandedSpreads);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedSpreads(next);
  };

  // Group open trades into spreads (from DB trades) OR from broker openPositions (live API)
  useEffect(() => {
    const groupTrades = () => {
      // When we have broker openPositions, use those as the source (positions come from API, not DB)
      if (openPositions.length > 0) {
        const stockLegs = [];
        const assignedStockLegs = [];
        const byExpiration = {};
        const stockHoldings = {};

        openPositions.forEach((pos, idx) => {
          const optionInfo = pos.optionInfo || parseOptionSymbol(pos.symbol);
          const openDate = pos.openDate ?? pos.date_acquired ?? pos.tradeDate ?? pos.trade_date ?? null;
          const position = {
            ...pos,
            optionInfo,
            cost: pos.cost ?? pos.cost_basis ?? pos.totalCost ?? 0,
            proceeds: pos.cost ?? pos.cost_basis ?? pos.totalCost ?? 0,
            tradeDate: openDate,
            openDate,
            id: pos.id ?? `broker-pos-${idx}`,
            symbol: pos.symbol,
            originalSymbol: pos.originalSymbol ?? pos.symbol
          };

          if (!optionInfo.isOption) {
            if (pos.isAssigned || pos.is_assigned) {
              assignedStockLegs.push({ type: 'individual', position, id: `assigned-stock-${idx}` });
            } else {
              stockLegs.push({ type: 'individual', position, id: `stock-${idx}` });
            }
            const sym = position.symbol;
            stockHoldings[sym] = (stockHoldings[sym] || 0) + (parseFloat(position.quantity) || 0);
            return;
          }

          const exp = optionInfo.expiration || 'Unknown';
          if (!byExpiration[exp]) byExpiration[exp] = [];
          byExpiration[exp].push(position);
        });

        const grouped = [];
        if (assignedStockLegs.length > 0) {
          grouped.push({ type: 'header', title: 'Assigned Positions' });
          grouped.push(...assignedStockLegs);
        }
        if (stockLegs.length > 0) {
          if (assignedStockLegs.length > 0) grouped.push({ type: 'header', title: 'Owned Positions' });
          grouped.push(...stockLegs);
        }

        const sortedExpirations = Object.keys(byExpiration).sort((a, b) => {
          if (a === 'Unknown' || b === 'Unknown') return 0;
          return new Date(a) - new Date(b);
        });
        sortedExpirations.forEach(exp => {
          const positions = byExpiration[exp];
          let pool = positions.map(p => ({ ...p, _used: false }));

          // Sort by strike
          pool.sort((a, b) => a.optionInfo.strike - b.optionInfo.strike);

          // Iterative spread matching
          for (let i = 0; i < pool.length; i++) {
            if (pool[i]._used) continue;

            let bestMatchIndex = -1;
            for (let j = i + 1; j < pool.length; j++) {
              if (pool[j]._used) continue;
              if (isVerticalSpread(pool[i], pool[j])) {
                bestMatchIndex = j;
                break;
              }
            }

            if (bestMatchIndex !== -1) {
              const leg1 = pool[i];
              const leg2 = pool[bestMatchIndex];
              pool[i]._used = true;
              pool[bestMatchIndex]._used = true;

              const spread = {
                type: 'spread',
                spreadType: getStrategyName([leg1, leg2]),
                legs: [leg1, leg2],
                expiration: exp,
                id: `spread-${exp}-${leg1.id}-${leg2.id}`
              };
              spread.metrics = calculateSpreadMetrics(spread);
              grouped.push(spread);
            }
          }

          // Add remaining as individual
          pool.filter(p => !p._used).forEach((p, i) => {
            let bpEffect = 0;
            const isShort = (p.quantity || 0) < 0;
            const absQty = Math.abs(p.quantity || 0);
            const strike = p.optionInfo?.strike || 0;
            if (p.optionInfo && isShort && strike > 0) {
              if (p.optionInfo.type === 'P') bpEffect = strike * absQty * 100;
              else if (p.optionInfo.type === 'C') {
                const underlying = p.optionInfo.underlying;
                const stockQty = stockHoldings[underlying] || 0;
                bpEffect = stockQty > 0 ? 0 : strike * absQty * 100;
              }
            }
            grouped.push({ type: 'individual', position: p, id: `pos-${exp}-${p.id || i}`, bpEffect });
          });
        });
        setGroupedOpenTrades(grouped);
        return;
      }

      // Fallback: use processedIndividualTrades (DB trades with Open status)
      const openTrades = processedIndividualTrades.filter(trade => 
        !trade.isOrderHeader && !trade._hideOpeningLeg && getTradeStatus(trade) === 'Open'
      );

      if (openTrades.length === 0) {
        setGroupedOpenTrades([]);
        return;
      }

      // 2. Parse option info
      const tradesWithInfo = openTrades.map(t => ({
        ...t,
        optionInfo: parseOptionSymbol(t.originalSymbol || t.original_symbol || t.symbol)
      }));

      // 3. Group by Expiration
      const byExpiration = {};
      const stockLegs = [];
      const assignedStockLegs = [];
      
      tradesWithInfo.forEach(trade => {
        if (!trade.optionInfo.isOption) {
          if (trade.isAssigned || trade.is_assigned) {
              assignedStockLegs.push({ type: 'individual', position: trade, id: `assigned-stock-${trade.id}` });
          } else {
              stockLegs.push({ type: 'individual', position: trade, id: `stock-${trade.id}` });
          }
          return;
        }
        
        const exp = trade.optionInfo.expiration || 'Unknown';
        if (!byExpiration[exp]) byExpiration[exp] = [];
        byExpiration[exp].push(trade);
      });

      const grouped = [];
      if (assignedStockLegs.length > 0) {
          grouped.push({ type: 'header', title: 'Assigned Positions' });
          grouped.push(...assignedStockLegs);
      }
      if (stockLegs.length > 0) {
          if (assignedStockLegs.length > 0) {
              grouped.push({ type: 'header', title: 'Owned Positions' });
          }
          grouped.push(...stockLegs);
      }
      
      // Create a map of stock holdings for covered call detection
      const stockHoldings = {};
      [...stockLegs, ...assignedStockLegs].forEach(leg => {
        if (leg && leg.position) {
          const sym = leg.position.symbol;
          stockHoldings[sym] = (stockHoldings[sym] || 0) + (parseFloat(leg.position.quantity) || 0);
        }
      });

      // 4. Detect spreads within expirations
      const sortedExpirations = Object.keys(byExpiration).sort((a, b) => {
        // Parse dates to sort correctly (assuming YYYY-MM-DD or similar, or just string compare)
        return new Date(a) - new Date(b);
      });

      sortedExpirations.forEach(exp => {
          const positions = byExpiration[exp];
          let pool = positions.map(p => ({ ...p, _used: false }));
          
          // Sort by strike
          pool.sort((a,b) => a.optionInfo.strike - b.optionInfo.strike);
          
          // Iterative Matching
          for (let i = 0; i < pool.length; i++) {
              if (pool[i]._used) continue;
              
              let bestMatchIndex = -1;
              for (let j = i + 1; j < pool.length; j++) {
                  if (pool[j]._used) continue;
                  if (isVerticalSpread(pool[i], pool[j])) {
                      bestMatchIndex = j;
                      break; 
                  }
              }
              
              if (bestMatchIndex !== -1) {
                  const leg1 = pool[i];
                  const leg2 = pool[bestMatchIndex];
                  pool[i]._used = true;
                  pool[bestMatchIndex]._used = true;
                  
                  const spread = {
                      type: 'spread',
                      spreadType: getStrategyName([leg1, leg2]),
                      legs: [leg1, leg2],
                      expiration: exp,
                      id: `spread-${exp}-${leg1.id}-${leg2.id}`
                  };
                  spread.metrics = calculateSpreadMetrics(spread);
                  grouped.push(spread);
              }
          }
          
          // Add remaining as individual
          pool.filter(p => !p._used).forEach(p => {
              let bpEffect = 0;
              const side = getTradeSide(p);
              const isShort = side === 'SHORT' || (side === 'UNKNOWN' && p.quantity < 0) || p.quantity < 0;
              const absQty = Math.abs(p.quantity);
              const strike = p.optionInfo?.strike || 0;
              
              if (p.optionInfo && isShort && strike > 0) {
                  if (p.optionInfo.type === 'P') {
                      // Short Put: Cash secured
                      bpEffect = strike * absQty * 100;
                  } else if (p.optionInfo.type === 'C') {
                      // Short Call: Check for underlying stock (Covered Call)
                      const underlying = p.optionInfo.underlying;
                      const stockQty = (typeof stockHoldings !== 'undefined' && stockHoldings[underlying]) ? stockHoldings[underlying] : 0;
                      
                      // If we hold stock, treat as covered (BP = 0)
                      if (stockQty > 0) {
                          bpEffect = 0;
                      } else {
                          // Naked Call: BP based on strike
                          bpEffect = strike * absQty * 100;
                      }
                  }
              }
              
              grouped.push({
                  type: 'individual',
                  position: p,
                  id: `pos-${p.id}`,
                  bpEffect
              });
          });
      });
      
      setGroupedOpenTrades(grouped);
    };

    groupTrades();
  }, [openPositions, processedIndividualTrades, quotesBySymbol]);

  // Compute total unrealized P/L from open positions (for summary card when API returns 0)
  const computedUnrealizedPL = useMemo(() => {
    let total = 0;
    groupedOpenTrades.forEach(g => {
      if (g.type === 'spread' && g.metrics) total += g.metrics.unrealizedPL || 0;
      else if (g.type === 'individual' && g.position) total += calculateUnrealizedPL(g.position);
    });
    return total;
  }, [groupedOpenTrades, quotesBySymbol]);

  // Group CLOSED trades into spreads
  useEffect(() => {
    const groupClosedTrades = () => {
      // 1. Get all closed trades
      const closedTrades = processedIndividualTrades.filter(trade => 
        !trade.isOrderHeader && !trade._hideOpeningLeg && !trade._hideDueToAggregation && getTradeStatus(trade) === 'Closed'
      );

      if (closedTrades.length === 0) {
        setGroupedClosedTrades([]);
        return;
      }

      // 2. Parse option info
      const tradesWithInfo = closedTrades.map(t => ({
        ...t,
        optionInfo: parseOptionSymbol(t.originalSymbol || t.symbol)
      }));

      // 3. Group by Expiration
      const byExpiration = {};
      const stockLegs = [];
      
      tradesWithInfo.forEach(trade => {
        if (!trade.optionInfo.isOption) {
          stockLegs.push({ type: 'individual', position: trade, id: `closed-stock-${trade.id}` });
          return;
        }
        
        const exp = trade.optionInfo.expiration || 'Unknown';
        if (!byExpiration[exp]) byExpiration[exp] = [];
        byExpiration[exp].push(trade);
      });

      const grouped = [...stockLegs];

      // 4. Detect spreads within expirations
      Object.entries(byExpiration).forEach(([exp, positions]) => {
          let pool = positions.map(p => ({ ...p, _used: false }));
          
          // Sort by strike
          pool.sort((a,b) => a.optionInfo.strike - b.optionInfo.strike);
          
          // Iterative Matching
          for (let i = 0; i < pool.length; i++) {
              if (pool[i]._used) continue;
              
              let bestMatchIndex = -1;
              for (let j = i + 1; j < pool.length; j++) {
                  if (pool[j]._used) continue;
                  if (isVerticalSpread(pool[i], pool[j])) {
                      bestMatchIndex = j;
                      break; 
                  }
              }
              
              if (bestMatchIndex !== -1) {
                  const leg1 = pool[i];
                  const leg2 = pool[bestMatchIndex];
                  pool[i]._used = true;
                  pool[bestMatchIndex]._used = true;
                  
                  const spread = {
                      type: 'spread',
                      spreadType: getStrategyName([leg1, leg2]),
                      legs: [leg1, leg2],
                      expiration: exp,
                      id: `closed-spread-${exp}-${leg1.id}-${leg2.id}`
                  };
                  spread.metrics = calculateClosedSpreadMetrics(spread);
                  grouped.push(spread);
              }
          }
          
          // Add remaining as individual
          pool.filter(p => !p._used).forEach(p => {
              grouped.push({
                  type: 'individual',
                  position: p,
                  id: `closed-pos-${p.id}`
              });
          });
      });
      
      // Sort: Stocks first, then by closing date desc
      grouped.sort((a, b) => {
        const getDate = (item) => {
          if (item.type === 'spread') {
            // For spreads, use the latest closing date of any leg
            return item.legs.reduce((max, leg) => {
              const d = new Date(leg.closingDate || leg.tradeDate || 0);
              return d > max ? d : max;
            }, new Date(0));
          }
          return new Date(item.position.closingDate || item.position.tradeDate || 0);
        };

        const dateA = getDate(a);
        const dateB = getDate(b);
        return dateB - dateA;
      });
      
      setGroupedClosedTrades(grouped);
    };

    groupClosedTrades();
  }, [processedIndividualTrades]);

  // Calculate Option Status (ITM/OTM)
  const calculateOptionStatus = (position) => {
    if (!position.optionInfo || !position.optionInfo.isOption) return 'N/A';
    
    const underlyingSym = position.optionInfo.underlying;
    const underlyingQuote = quotesBySymbol[underlyingSym];
    
    if (!underlyingQuote) return '-';
    
    const currentPrice = parseFloat(underlyingQuote.last || underlyingQuote.close || 0);
    if (!currentPrice) return '-';
    
    const strike = parseFloat(position.optionInfo.strike);
    const type = position.optionInfo.type; // 'C' or 'P'
    
    let status = '';
    let distance = 0;
    
    if (type === 'C') {
        if (currentPrice > strike) {
            status = 'ITM';
            distance = (currentPrice - strike) / strike;
        } else {
            status = 'OTM';
            distance = (strike - currentPrice) / strike;
        }
    } else {
        if (currentPrice < strike) {
            status = 'ITM';
            distance = (strike - currentPrice) / strike;
        } else {
            status = 'OTM';
            distance = (currentPrice - strike) / strike;
        }
    }
    
    return { status, distance };
  };

  const getOptionStatusDisplay = (position) => {
      const result = calculateOptionStatus(position);
      if (typeof result === 'string') return result;
      
      const color = result.status === 'ITM' ? 'warning.main' : 'text.secondary';
      return (
          <Typography variant="body2" sx={{ color, fontWeight: result.status === 'ITM' ? 'bold' : 'normal' }}>
              {result.status}
          </Typography>
      );
  };

  const hasShortLegITM = (spread) => {
      if (!spread || !Array.isArray(spread.legs)) return false;
      return spread.legs.some(leg => {
          const side = getTradeSide(leg);
          if (side !== 'SHORT') return false;
          const result = calculateOptionStatus(leg);
          if (typeof result === 'string') return result === 'ITM';
          return result && result.status === 'ITM';
      });
  };

  const { privacyMask } = usePrivacyMask();

  const formatCurrency = (value) => {
    if (privacyMask) return '••••••';
    if (value === null || value === undefined || isNaN(value)) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00%';
    }
    return `${parseFloat(value).toFixed(2)}%`;
  };

  const formatCompact = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '—';
    const abs = Math.abs(value);
    if (abs >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
    return formatCurrency(value);
  };

  const relativeTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const days = Math.round((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.round(days / 30)} mo ago`;
    return `${(days / 365).toFixed(1)} yrs ago`;
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
  };

  const consensusMeta = {
    strong_buy: { label: 'Strong Buy', color: '#1b8a3d' },
    buy: { label: 'Buy', color: '#66bb6a' },
    hold: { label: 'Hold', color: '#f0a202' },
    sell: { label: 'Sell', color: '#e57373' },
    strong_sell: { label: 'Strong Sell', color: '#c62828' },
  };
  const getConsensusMeta = (key) => consensusMeta[key] || consensusMeta.hold;

  const stockOptions = React.useMemo(() => {
    const bySymbol = new Map();
    stockList.forEach(opt => {
      if (opt && opt.symbol) bySymbol.set(opt.symbol, opt);
    });
    recentSymbols.forEach(sym => {
      if (sym && !bySymbol.has(sym)) bySymbol.set(sym, { symbol: sym, name: sym });
    });
    return Array.from(bySymbol.values());
  }, [stockList, recentSymbols]);

  // Normalize open positions into per-share price levels for the chart's
  // cost-basis lines: shares → avg cost; options → breakeven (strike ± premium).
  const chartPositionLines = React.useMemo(() => {
    const lines = [];
    (groupedOpenTrades || []).forEach(row => {
      const list = row.type === 'individual'
        ? [row.position]
        : (row.legs || []).map(l => l.position || l).filter(Boolean);
      list.forEach(p => {
        if (!p) return;
        const qty = Math.abs(parseFloat(p.quantity) || 0);
        if (!qty) return;
        const info = p.optionInfo || parseOptionSymbol(p.originalSymbol || p.symbol);
        const unitCost = Math.abs(parseFloat(p.cost) || 0) / qty / (info.isOption ? 100 : 1);
        let basis;
        if (!info.isOption) {
          basis = unitCost;
        } else if (info.strike != null) {
          const isCall = String(info.type || '').toUpperCase().startsWith('C');
          basis = isCall ? info.strike + unitCost : info.strike - unitCost;
        }
        if (basis > 0) lines.push({ quantity: 1, averagePrice: basis });
      });
    });
    return lines;
  }, [groupedOpenTrades]);

  // Analyst gauge segments + target-range markers for the research column
  const aratings = analysisData?.analystRatings || null;
  const analystTotal = aratings
    ? (aratings.strongBuyCount || 0) + (aratings.buyCount || 0) + (aratings.holdCount || 0) + (aratings.sellCount || 0) + (aratings.strongSellCount || 0)
    : 0;
  const analystSegs = aratings ? [
    { label: 'Strong Buy', count: aratings.strongBuyCount || 0, color: '#1b8a3d' },
    { label: 'Buy', count: aratings.buyCount || 0, color: '#66bb6a' },
    { label: 'Hold', count: aratings.holdCount || 0, color: '#f0a202' },
    { label: 'Sell', count: aratings.sellCount || 0, color: '#e57373' },
    { label: 'Strong Sell', count: aratings.strongSellCount || 0, color: '#c62828' },
  ] : [];
  const targetMarkers = (() => {
    if (!aratings || aratings.targetPriceLow == null || aratings.targetPriceHigh == null) return [];
    const lo = aratings.targetPriceLow;
    const hi = aratings.targetPriceHigh;
    const span = Math.max(0.0001, hi - lo);
    const ms = [];
    if (aratings.targetPriceMean != null) {
      ms.push({ label: 'Avg Target', value: aratings.targetPriceMean, pct: ((aratings.targetPriceMean - lo) / span) * 100, color: '#f0a202', main: true });
    }
    if (analysisData?.currentPrice != null) {
      ms.push({ label: 'Current', value: analysisData.currentPrice, pct: ((analysisData.currentPrice - lo) / span) * 100, color: '#1976d2', main: false });
    }
    return ms;
  })();

  // Zeroed stats so a symbol with no trades still renders quote/analysis
  // instead of hitting the "No data" gate; real data overrides wholesale.
  const perfStats = stockData || {
    totalPL: 0, realizedPL: 0, unrealizedPL: 0, tradeCount: 0,
    winCount: 0, lossCount: 0, grossProfit: 0, grossLoss: 0,
    winRate: 0, avgWin: 0, avgLoss: 0, profitFactor: 0,
    dividend: 0, firstTradeDate: null, lastTradeDate: null,
  };

  return (
    <Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            freeSolo
            fullWidth
            value={selectedStock}
            onChange={handleStockChange}
            options={stockOptions}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (option.name && option.name !== option.symbol) {
                return `${option.symbol} - ${option.name}`;
              }
              return option.symbol;
            }}
            renderInput={(params) => <TextField {...params} label="Select Stock" variant="outlined" fullWidth />}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              const optSym = typeof option === 'string' ? option : option.symbol;
              const valSym = typeof value === 'string' ? value : value.symbol;
              return optSym === valSym;
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Account</InputLabel>
              <Select
                multiple
                value={draftAccount}
                onChange={handleAccountChange}
                input={<OutlinedInput label="Account" />}
                renderValue={(selected) => {
                  if (selected.includes('all')) return 'All Accounts';
                  if (selected.length === 1) {
                    const acc = accounts.find(a => a.id === selected[0]);
                    return acc ? acc.name : selected[0];
                  }
                  return `${selected.length} Accounts`;
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={draftAccount.includes('all')} />
                  <ListItemText primary="All Accounts" />
                </MenuItem>
                
                {taxableAccounts.length > 0 && [
                  <ListSubheader 
                    key="taxable-header" 
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAccountCategorySelect('taxable');
                    }}
                    sx={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: 'rgba(0,0,0,0.05)', 
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
                      pointerEvents: 'auto'
                    }}
                  >
                    <Checkbox 
                      size="small" 
                      checked={taxableAccounts.length > 0 && taxableAccounts.every(a => draftAccount.includes(a.id))} 
                      indeterminate={taxableAccounts.some(a => draftAccount.includes(a.id)) && !taxableAccounts.every(a => draftAccount.includes(a.id))}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                    Taxable Accounts
                  </ListSubheader>,
                  ...taxableAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      <Checkbox checked={draftAccount.indexOf(account.id) > -1} />
                      <ListItemText primary={account.name} />
                    </MenuItem>
                  ))
                ]}

                {nonTaxableAccounts.length > 0 && [
                  <ListSubheader 
                    key="non-taxable-header" 
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAccountCategorySelect('non-taxable');
                    }}
                    sx={{ 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: 'rgba(0,0,0,0.05)', 
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
                      pointerEvents: 'auto'
                    }}
                  >
                    <Checkbox 
                      size="small" 
                      checked={nonTaxableAccounts.length > 0 && nonTaxableAccounts.every(a => draftAccount.includes(a.id))} 
                      indeterminate={nonTaxableAccounts.some(a => draftAccount.includes(a.id)) && !nonTaxableAccounts.every(a => draftAccount.includes(a.id))}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                    Non-Taxable Accounts
                  </ListSubheader>,
                  ...nonTaxableAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      <Checkbox checked={draftAccount.indexOf(account.id) > -1} />
                      <ListItemText primary={account.name} />
                    </MenuItem>
                  ))
                ]}

                {/* Show uncategorized if any (fallback) */}
                {accounts.filter(a => !a.accountType).map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Checkbox checked={draftAccount.indexOf(account.id) > -1} />
                    <ListItemText primary={account.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              onClick={applyAccountSelection}
              variant="contained"
              size="small"
              disabled={JSON.stringify(draftAccount.sort()) === JSON.stringify(selectedAccount.sort())}
              sx={{ minWidth: '40px', px: 2 }}
            >
              OK
            </Button>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={handleTimePeriodChange}
              label="Time Period"
            >
              <MenuItem value="current_month">Current Month</MenuItem>
              <MenuItem value="previous_month">Previous Month</MenuItem>
              <MenuItem value="30d">30 Days</MenuItem>
              <MenuItem value="60d">60 Days</MenuItem>
              <MenuItem value="90d">90 Days</MenuItem>
              <MenuItem value="180d">180 Days</MenuItem>
              <MenuItem value="ytd">Year to Date</MenuItem>
              <MenuItem value="last_year">Last Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {timePeriod === 'custom' && (
          <>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {!selectedStock ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center">
              Please select a stock to view analysis
            </Typography>
          </CardContent>
        </Card>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : !stockData && !analysisData ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No data available for this stock.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Hero Company Snapshot */}
          {analysisData && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                      {analysisData.symbol}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {analysisData.companyName
                        || (selectedStock?.name && selectedStock.name !== selectedStock.symbol ? selectedStock.name : '')}
                    </Typography>
                  </Box>
                  {analysisData.currentPrice != null && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                        {formatCurrency(analysisData.currentPrice)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={(analysisData.dayChangePercent ?? 0) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(analysisData.dayChange || 0)}{' '}
                        ({(analysisData.dayChangePercent ?? 0) >= 0 ? '+' : ''}{(analysisData.dayChangePercent ?? 0).toFixed(2)}%)
                      </Typography>
                    </Box>
                  )}
                  {analysisData.fiftyTwoWeekLow != null && analysisData.fiftyTwoWeekHigh != null && analysisData.currentPrice != null && (
                    <Box sx={{ flexGrow: 1, minWidth: 180, px: 2 }}>
                      <Box sx={{ position: 'relative', height: 6, borderRadius: 3, background: 'linear-gradient(90deg, #e57373 0%, #f0a202 50%, #66bb6a 100%)' }}>
                        <Box sx={{
                          position: 'absolute',
                          left: `${Math.min(100, Math.max(0, ((analysisData.currentPrice - analysisData.fiftyTwoWeekLow) / Math.max(0.0001, analysisData.fiftyTwoWeekHigh - analysisData.fiftyTwoWeekLow)) * 100))}%`,
                          top: -4, width: 14, height: 14, borderRadius: '50%',
                          bgcolor: isDark ? '#e6e8ec' : '#1a1d23', transform: 'translateX(-50%)',
                          border: '2px solid', borderColor: 'background.paper'
                        }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{formatCurrency(analysisData.fiftyTwoWeekLow)}</Typography>
                        <Typography variant="caption" color="text.secondary">52-Wk Range</Typography>
                        <Typography variant="caption" color="text.secondary">{formatCurrency(analysisData.fiftyTwoWeekHigh)}</Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {analysisData.sector && <Chip label={analysisData.sector} size="small" variant="outlined" />}
                    {analysisData.industry && <Chip label={analysisData.industry} size="small" variant="outlined" />}
                    {analysisData.marketCap != null && <Chip label={`Mkt Cap ${formatCompact(analysisData.marketCap)}`} size="small" variant="outlined" />}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* KPI Grid */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography color="textSecondary" variant="caption" gutterBottom display="block">
                    Realized P/L
                  </Typography>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }} color={parseFloat(perfStats.realizedPL - (perfStats.dividend || 0)) >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(perfStats.realizedPL - (perfStats.dividend || 0))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Div: {formatCurrency(perfStats.dividend || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography color="textSecondary" variant="caption" gutterBottom display="block">
                    Unrealized P/L
                  </Typography>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }} color={parseFloat(groupedOpenTrades.length > 0 ? computedUnrealizedPL : (perfStats.unrealizedPL || 0)) >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(groupedOpenTrades.length > 0 ? computedUnrealizedPL : (perfStats.unrealizedPL || 0))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {groupedOpenTrades.length || 0} open
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography color="textSecondary" variant="caption" gutterBottom display="block">
                    Win Rate / Profit Factor
                  </Typography>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    {formatPercentage(perfStats.winRate)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PF {perfStats.profitFactor.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography color="textSecondary" variant="caption" gutterBottom display="block">
                    Analyst Consensus
                  </Typography>
                  {analysisData?.analystRatings?.consensus ? (
                    <>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: getConsensusMeta(analysisData.analystRatings.consensus).color }}>
                        {getConsensusMeta(analysisData.analystRatings.consensus).label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Score {analysisData.analystRatings.meanScore?.toFixed(1) || '—'}/5
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h6" color="text.secondary">—</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography color="textSecondary" variant="caption" gutterBottom display="block">
                    Mean Price Target
                  </Typography>
                  {analysisData?.analystRatings?.targetPriceMean != null ? (
                    <>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(analysisData.analystRatings.targetPriceMean)}
                      </Typography>
                      {analysisData.currentPrice != null && (
                        <Typography
                          variant="caption"
                          color={analysisData.analystRatings.targetPriceMean >= analysisData.currentPrice ? 'success.main' : 'error.main'}
                        >
                          {analysisData.analystRatings.targetPriceMean >= analysisData.currentPrice ? '+' : ''}
                          {(((analysisData.analystRatings.targetPriceMean - analysisData.currentPrice) / analysisData.currentPrice) * 100).toFixed(1)}% vs current
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="h6" color="text.secondary">—</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Card>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography color="textSecondary" variant="caption" gutterBottom display="block">
                    Next Earnings
                  </Typography>
                  {analysisData?.fundamentals?.nextEarningsDate ? (
                    <>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        {new Date(analysisData.fundamentals.nextEarningsDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        in {daysUntil(analysisData.fundamentals.nextEarningsDate)}d
                        {analysisData.fundamentals.exDividendDate ? ` · ex-div ${new Date(analysisData.fundamentals.exDividendDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h6" color="text.secondary">—</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* LEFT: trading history & technicals (~65%) */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={leftTab} onChange={(_, v) => setLeftTab(v)}>
                  <Tab label="Price Chart &amp; Overlays" />
                  <Tab label="Positions &amp; Performance" />
                </Tabs>
              </Box>

              {leftTab === 0 && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <StockTradeChart
                    symbol={selectedStock.symbol}
                    trades={individualTrades}
                    positions={chartPositionLines}
                    height={420}
                  />
                </CardContent>
              </Card>
              )}

          {leftTab === 1 && (<>
          {/* Additional Metrics */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Metrics
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    Average Win
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(perfStats.avgWin)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    Average Loss
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(perfStats.avgLoss)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    Win/Loss Ratio
                  </Typography>
                  <Typography variant="body1">
                    {(perfStats.avgWin / (perfStats.avgLoss || 1)).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    Winning Trades
                  </Typography>
                  <Typography variant="body1">
                    {perfStats.winCount}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    Losing Trades
                  </Typography>
                  <Typography variant="body1">
                    {perfStats.lossCount}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    First Trade
                  </Typography>
                  <Typography variant="body1">
                    {perfStats.firstTradeDate ? new Date(perfStats.firstTradeDate).toLocaleDateString() : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Positions & Trades — open and closed positions shown as separate tables */}
          {(groupedOpenTrades.length > 0 || groupedClosedTrades.length > 0) ? (() => {
                const headerBg = isDark ? 'rgba(255,255,255,0.07)' : '#f0f0f0';

                const posInfo = (p) => p.optionInfo || parseOptionSymbol(p.originalSymbol || p.symbol);
                const posTypeLabel = (p) => {
                  const info = posInfo(p);
                  const side = getTradeSide(p);
                  if (!info.isOption) return side === 'SHORT' ? 'Short Stock' : 'Long Stock';
                  return `${side === 'SHORT' ? 'Short' : 'Long'} ${info.type === 'C' ? 'Call' : 'Put'}`;
                };
                const expStr = (p) => {
                  const info = posInfo(p);
                  if (!info.isOption || !info.expiration) return '—';
                  const e = info.expiration;
                  return `${e.substring(2, 4)}/${e.substring(4, 6)}/${e.substring(0, 2)}`;
                };
                const openDateStr = (p) => (p.openDate || p.tradeDate) ? formatShortDate(p.openDate || p.tradeDate) : '—';
                const closeDateStr = (p) => (p.closingDate || p.tradeDate) ? formatShortDate(p.closingDate || p.tradeDate) : '—';
                const priceSub = (p) => {
                  const info = posInfo(p);
                  const sym = info.isOption ? info.underlying : info.underlying || p.symbol;
                  const q = quotesBySymbol[sym];
                  const last = q ? parseFloat(q.last ?? q.mark ?? q.lastPrice ?? q.close) : NaN;
                  if (isNaN(last)) return null;
                  return <div style={{ fontSize: '0.75em', color: '#888' }}>{sym} ${last.toFixed(2)}</div>;
                };
                const pctChip = (pl, basis) => {
                  if (!basis || !isFinite(basis)) return null;
                  const pct = (pl / Math.abs(basis)) * 100;
                  return (
                    <div style={{ fontSize: '0.75em', color: pl >= 0 ? '#2e7d32' : '#c62828' }}>
                      {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
                    </div>
                  );
                };
                const spreadExpiration = (group) => {
                  const info = (group.legs || []).map(l => l.optionInfo || parseOptionSymbol(l.originalSymbol || l.symbol)).find(i => i.isOption);
                  if (!info || !info.expiration) return '—';
                  const e = info.expiration;
                  return `${e.substring(2, 4)}/${e.substring(4, 6)}/${e.substring(0, 2)}`;
                };
                const spreadOpenDate = (group) => {
                  const d = (group.legs || []).map(l => l.openDate || l.tradeDate).filter(Boolean).sort()[0];
                  return d ? formatShortDate(d) : '—';
                };
                const spreadCloseDate = (group) => {
                  const ds = (group.legs || []).map(l => l.closingDate || l.tradeDate).filter(Boolean).sort();
                  return ds.length ? formatShortDate(ds[ds.length - 1]) : '—';
                };

                // Section totals
                let openCount = 0, openMV = 0, openPL = 0;
                groupedOpenTrades.forEach(g => {
                  if (g.type === 'header') return;
                  openCount += 1;
                  if (g.type === 'spread') {
                    openMV += g.metrics?.totalMarketValue || 0;
                    openPL += g.metrics?.unrealizedPL || 0;
                  } else if (g.position) {
                    openMV += parseFloat(g.position.marketValue) || 0;
                    openPL += calculateUnrealizedPL(g.position);
                  }
                });
                let closedCount = 0, closedDiv = 0, closedPL = 0;
                groupedClosedTrades.forEach(g => {
                  closedCount += 1;
                  if (g.type === 'spread') {
                    closedDiv += g.metrics?.totalDividend || 0;
                    closedPL += (g.metrics?.netPL || 0) + (g.metrics?.totalDividend || 0);
                  } else if (g.position) {
                    closedDiv += parseFloat(g.position.dividend) || 0;
                    closedPL += getRealizedPLForDisplay(g.position);
                  }
                });
                const totalPL = openPL + closedPL;

                return (
                  <>
                    {groupedOpenTrades.length > 0 && (
                      <Card sx={{ mb: 4 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Open Positions
                            <Chip label={`${openCount} position${openCount === 1 ? '' : 's'}`} size="small" sx={{ ml: 1.5 }} />
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                              MV {formatCurrency(openMV)} · Unrealized P/L {formatCurrency(openPL)}
                            </Typography>
                          </Typography>
                          <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell width="40px" />
                                  <TableCell><strong>Symbol/Spread</strong></TableCell>
                                  <TableCell><strong>Type</strong></TableCell>
                                  <TableCell align="right"><strong>Qty</strong></TableCell>
                                  <TableCell align="right"><strong>Opened</strong></TableCell>
                                  <TableCell align="right"><strong>Expiration</strong></TableCell>
                                  <TableCell align="right"><strong>Market Value</strong></TableCell>
                                  <TableCell align="right"><strong>Unrealized P/L</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {groupedOpenTrades.map((group) => {
                          if (group.type === 'header') {
                            return (
                              <TableRow key={`header-${group.title}`} sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa' }}>
                                <TableCell colSpan={8} sx={{ fontWeight: 600, fontStyle: 'italic', color: 'text.secondary' }}>
                                  {group.title}
                                </TableCell>
                              </TableRow>
                            );
                          }
                          if (group.type === 'spread') {
                            const expanded = expandedSpreads.has(group.id);
                            const uPL = group.metrics.unrealizedPL;
                            const basis = Math.abs(group.metrics.netDebit) || Math.abs(group.metrics.totalCost) || 0;
                            return (
                              <React.Fragment key={group.id}>
                                <TableRow hover sx={{ backgroundColor: hasShortLegITM(group) ? 'rgba(244, 67, 54, 0.12)' : 'rgba(76, 175, 80, 0.12)' }}>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => toggleSpreadExpansion(group.id)}>
                                      {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="subtitle2" fontWeight="bold">{formatSpreadLabel(group)}</Typography>
                                    {priceSub(group.legs[0] || {})}
                                  </TableCell>
                                  <TableCell><Chip label={group.spreadType} size="small" color="primary" variant="outlined" /></TableCell>
                                  <TableCell align="right">{group.legs.length} legs</TableCell>
                                  <TableCell align="right">{spreadOpenDate(group)}</TableCell>
                                  <TableCell align="right">{spreadExpiration(group)}</TableCell>
                                  <TableCell align="right">{formatCurrency(group.metrics.totalMarketValue)}</TableCell>
                                  <TableCell align="right">
                                    <Typography color={uPL >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                      {formatCurrency(uPL)}
                                    </Typography>
                                    {pctChip(uPL, basis)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 1 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Leg</TableCell>
                                              <TableCell align="right">Qty</TableCell>
                                              <TableCell align="right">Cost/Proceeds</TableCell>
                                              <TableCell align="right">P/L</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {group.legs.map((leg, idx) => {
                                              const luPL = calculateUnrealizedPL(leg);
                                              return (
                                                <TableRow key={idx}>
                                                  <TableCell>{formatOptionLabel(leg)}</TableCell>
                                                  <TableCell align="right">{leg.quantity}</TableCell>
                                                  <TableCell align="right">{formatCurrency(leg.cost || leg.proceeds)}</TableCell>
                                                  <TableCell align="right">
                                                    <span style={{ color: luPL >= 0 ? 'green' : 'red' }}>{formatCurrency(luPL)}</span>
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          }
                          const pos = group.position;
                          const uPL = calculateUnrealizedPL(pos);
                          const basis = Math.abs(parseFloat(pos.cost) || 0);
                          return (
                            <TableRow key={group.id} hover>
                              <TableCell>
                                {!posInfo(pos).isOption && !pos.isAssigned && (
                                  <IconButton size="small" onClick={() => handleOpenAssignDialog(pos)} title="Mark as Assigned">
                                    <FlagIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                              <TableCell>
                                <strong>{formatOptionLabel(pos)}</strong>
                                {priceSub(pos)}
                              </TableCell>
                              <TableCell><Chip label={posTypeLabel(pos)} size="small" variant="outlined" /></TableCell>
                              <TableCell align="right">{pos.quantity}</TableCell>
                              <TableCell align="right">{openDateStr(pos)}</TableCell>
                              <TableCell align="right">{expStr(pos)}</TableCell>
                              <TableCell align="right">{formatCurrency(pos.marketValue || 0)}</TableCell>
                              <TableCell align="right">
                                <Typography color={uPL >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                  {formatCurrency(uPL)}
                                </Typography>
                                {pctChip(uPL, basis)}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                                {/* Open positions totals */}
                                <TableRow sx={{ backgroundColor: headerBg }}>
                                  <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>
                                    Total Open Positions
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(openMV)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight="bold" color={openPL >= 0 ? 'success.main' : 'error.main'}>
                                      {formatCurrency(openPL)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    )}

                    {groupedClosedTrades.length > 0 && (
                      <Card sx={{ mb: 4 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Closed Positions
                            <Chip label={`${closedCount} position${closedCount === 1 ? '' : 's'}`} size="small" sx={{ ml: 1.5 }} />
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                              Realized {formatCurrency(closedPL)}{closedDiv !== 0 && ` · incl. ${formatCurrency(closedDiv)} div`}
                            </Typography>
                          </Typography>
                          <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell width="40px" />
                                  <TableCell><strong>Symbol/Spread</strong></TableCell>
                                  <TableCell><strong>Type</strong></TableCell>
                                  <TableCell align="right"><strong>Qty</strong></TableCell>
                                  <TableCell align="right"><strong>Opened</strong></TableCell>
                                  <TableCell align="right"><strong>Closed</strong></TableCell>
                                  <TableCell align="right"><strong>Realized P/L</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {groupedClosedTrades.map((group) => {
                          if (group.type === 'spread') {
                            const expanded = expandedSpreads.has(group.id);
                            const effectivePL = (group.metrics.netPL || 0) + (group.metrics.totalDividend || 0);
                            return (
                              <React.Fragment key={group.id}>
                                <TableRow hover>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => toggleSpreadExpansion(group.id)}>
                                      {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="subtitle2" fontWeight="bold">{formatSpreadLabel(group)}</Typography>
                                  </TableCell>
                                  <TableCell><Chip label={group.spreadType} size="small" color="secondary" variant="outlined" /></TableCell>
                                  <TableCell align="right">{group.legs.length} legs</TableCell>
                                  <TableCell align="right">{spreadOpenDate(group)}</TableCell>
                                  <TableCell align="right">{spreadCloseDate(group)}</TableCell>
                                  <TableCell align="right">
                                    <Typography color={effectivePL >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                      {formatCurrency(effectivePL)}
                                    </Typography>
                                    {pctChip(effectivePL, Math.abs(group.metrics.totalCost))}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 1 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Leg</TableCell>
                                              <TableCell align="right">Qty</TableCell>
                                              <TableCell align="right">Cost</TableCell>
                                              <TableCell align="right">Proceeds</TableCell>
                                              <TableCell align="right">Dividend</TableCell>
                                              <TableCell align="right">P/L</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {group.legs.map((leg, idx) => (
                                              <TableRow key={idx}>
                                                <TableCell>{formatOptionLabel(leg)}</TableCell>
                                                <TableCell align="right">{leg.quantity}</TableCell>
                                                <TableCell align="right">{formatCurrency(leg.cost)}</TableCell>
                                                <TableCell align="right">{formatCurrency(leg.proceeds)}</TableCell>
                                                <TableCell align="right">{formatCurrency(leg.dividend || 0)}</TableCell>
                                                <TableCell align="right">
                                                  <span style={{ color: getRealizedPLForDisplay(leg) >= 0 ? 'green' : 'red' }}>
                                                    {formatCurrency(getRealizedPLForDisplay(leg))}
                                                  </span>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          }
                          const pos = group.position;
                          const realized = getRealizedPLForDisplay(pos);
                          return (
                            <TableRow key={group.id} hover>
                              <TableCell />
                              <TableCell>
                                <strong>{formatOptionLabel(pos)}</strong>
                                {priceSub(pos)}
                              </TableCell>
                              <TableCell><Chip label={posTypeLabel(pos)} size="small" variant="outlined" /></TableCell>
                              <TableCell align="right">{pos.quantity}</TableCell>
                              <TableCell align="right">{openDateStr(pos)}</TableCell>
                              <TableCell align="right">{closeDateStr(pos)}</TableCell>
                              <TableCell align="right">
                                <Typography color={realized >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                  {formatCurrency(realized)}
                                </Typography>
                                {pctChip(realized, Math.abs(parseFloat(pos.cost) || 0))}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                                {/* Closed positions totals */}
                                <TableRow sx={{ backgroundColor: headerBg }}>
                                  <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>
                                    Total Closed Positions
                                    {closedDiv !== 0 && (
                                      <span style={{ fontWeight: 'normal', fontSize: '0.8em', color: '#888' }}>
                                        {' '}· incl. {formatCurrency(closedDiv)} dividends
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight="bold" color={closedPL >= 0 ? 'success.main' : 'error.main'}>
                                      {formatCurrency(closedPL)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                {groupedOpenTrades.length > 0 && (
                                  <TableRow sx={{ backgroundColor: headerBg }}>
                                    <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>
                                      Combined (Open Unrealized + Closed Realized)
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontWeight="bold" color={totalPL >= 0 ? 'success.main' : 'error.main'}>
                                        {formatCurrency(totalPL)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    )}
                  </>
                );
              })() : (
                <Card sx={{ mb: 4 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Positions &amp; Trades
                    </Typography>
                    <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        No positions or closed trades found for this stock.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}


          {/* P/L Chart */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                P/L Performance Over Time
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Shows cumulative P/L trend and individual period performance including unrealized gains/losses
              </Typography>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={tradesByPeriod}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis 
                      dataKey="period" 
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                      }}
                      interval={0}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(label) => {
                        const date = new Date(label);
                        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      }}
                      contentStyle={{
                        backgroundColor: isDark ? '#1e222b' : '#ffffff',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
                        borderRadius: 6,
                        color: isDark ? '#e6e8ec' : 'inherit',
                      }}
                      labelStyle={{ color: isDark ? '#e6e8ec' : 'inherit' }}
                      itemStyle={{ color: isDark ? '#e6e8ec' : 'inherit' }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="cumulativePL"
                      name="Cumulative P/L"
                      stroke="#8884d8"
                      activeDot={{ r: 8 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="periodPL"
                      name="Period P/L"
                      stroke="#82ca9d"
                      strokeDasharray="5 5"
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
          </>)}
            </Grid>

            {/* RIGHT: research & fundamentals (~35%) */}
            <Grid size={{ xs: 12, lg: 4 }}>
              {/* Company Overview & Valuation */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Company Overview
                  </Typography>
                  {!analysisData ? (
                    <Typography variant="body2" color="text.secondary">
                      Research data unavailable
                    </Typography>
                  ) : (
                    <>
                      {analysisData.synopsis && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {analysisData.synopsis.split('. ').slice(0, 3).join('. ')}
                          {analysisData.synopsis.split('. ').length > 3 ? '.' : ''}
                        </Typography>
                      )}
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Trailing P/E</Typography>
                          <Typography variant="body2">{analysisData.fundamentals?.peRatio != null ? analysisData.fundamentals.peRatio.toFixed(1) : '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Forward P/E</Typography>
                          <Typography variant="body2">{analysisData.fundamentals?.forwardPe != null ? analysisData.fundamentals.forwardPe.toFixed(1) : '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Price / Sales</Typography>
                          <Typography variant="body2">{analysisData.fundamentals?.psRatio != null ? analysisData.fundamentals.psRatio.toFixed(2) : '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Gross Margin</Typography>
                          <Typography variant="body2">{analysisData.fundamentals?.grossMargin != null ? `${(analysisData.fundamentals.grossMargin * 100).toFixed(1)}%` : '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Debt / Equity</Typography>
                          <Typography variant="body2">{analysisData.fundamentals?.debtToEquity != null ? analysisData.fundamentals.debtToEquity.toFixed(1) : '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Short % of Float</Typography>
                          <Typography variant="body2">{analysisData.fundamentals?.shortInterestFloat != null ? `${(analysisData.fundamentals.shortInterestFloat * 100).toFixed(1)}%` : '—'}</Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Analyst Sentiment & Ratings */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Analyst Ratings
                  </Typography>
                  {!analysisData?.analystRatings ? (
                    <Typography variant="body2" color="text.secondary">No analyst data</Typography>
                  ) : (
                    <>
                      {analystTotal > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', height: 16, borderRadius: 1, overflow: 'hidden' }}>
                            {analystSegs.map(s => s.count > 0 && (
                              <Box key={s.label} sx={{ width: `${(s.count / analystTotal) * 100}%`, bgcolor: s.color }} />
                            ))}
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                            {analystSegs.map(s => s.count > 0 && (
                              <Typography key={s.label} variant="caption" color="text.secondary">
                                {s.label} {s.count}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {analysisData.analystRatings.targetPriceLow != null && analysisData.analystRatings.targetPriceHigh != null && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Price Target Range
                          </Typography>
                          <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)' }}>
                            {targetMarkers.map(m => (
                              <Box key={m.label} sx={{
                                position: 'absolute',
                                left: `${Math.min(100, Math.max(0, m.pct))}%`,
                                top: -4, width: m.main ? 14 : 10, height: m.main ? 14 : 10,
                                borderRadius: '50%', bgcolor: m.color, transform: 'translateX(-50%)',
                                border: '1px solid', borderColor: 'background.paper',
                              }} title={`${m.label}: ${formatCurrency(m.value)}`} />
                            ))}
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                            <Typography variant="caption" color="text.secondary">Low {formatCurrency(analysisData.analystRatings.targetPriceLow)}</Typography>
                            {analysisData.analystRatings.targetPriceMean != null && (
                              <Typography variant="caption" color="text.secondary">Avg {formatCurrency(analysisData.analystRatings.targetPriceMean)}</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">High {formatCurrency(analysisData.analystRatings.targetPriceHigh)}</Typography>
                          </Box>
                        </Box>
                      )}
                      {analysisData.upgrades?.length > 0 ? (
                        <Box>
                          <Divider sx={{ mb: 1 }} />
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Recent Rating Activity
                          </Typography>
                          {analysisData.upgrades.slice(0, 6).map((u, i) => (
                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.25 }}>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {u.action && <span style={{ textTransform: 'capitalize' }}>{u.action} </span>}
                                {u.fromGrade && u.toGrade ? `${u.fromGrade} → ${u.toGrade}` : (u.toGrade || u.fromGrade || '')}
                                {u.firm ? ` · ${u.firm}` : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                                {relativeTime(u.date)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : analysisData.commentary?.length > 0 && (
                        <Box>
                          <Divider sx={{ mb: 1 }} />
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Analyst Commentary
                          </Typography>
                          {analysisData.commentary.slice(0, 5).map((c, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
                              <Chip
                                label={c.type}
                                size="small"
                                variant="outlined"
                                color={c.type === 'buy' ? 'success' : c.type === 'sell' ? 'error' : 'default'}
                                sx={{ textTransform: 'capitalize', minWidth: 48 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                                {c.text}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* News & Catalysts */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    News &amp; Catalysts
                  </Typography>
                  {!analysisData?.news?.length ? (
                    <Typography variant="body2" color="text.secondary">No recent news</Typography>
                  ) : (
                    <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
                      {analysisData.news.map((n, i) => (
                        <Box key={i} sx={{
                          mb: 1.5, pb: 1.5,
                          borderBottom: i < analysisData.news.length - 1
                            ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                            : 'none'
                        }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Typography
                              variant="body2"
                              component={n.link ? 'a' : 'div'}
                              href={n.link || undefined}
                              target={n.link ? '_blank' : undefined}
                              rel={n.link ? 'noopener noreferrer' : undefined}
                              sx={{
                                flexGrow: 1,
                                color: 'text.primary',
                                textDecoration: 'none',
                                '&:hover': n.link ? { textDecoration: 'underline' } : {},
                              }}
                            >
                              {n.title}
                            </Typography>
                            <Chip
                              label={n.sentiment}
                              size="small"
                              variant="outlined"
                              color={n.sentiment === 'bullish' ? 'success' : n.sentiment === 'bearish' ? 'error' : 'default'}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {n.publisher}{n.publishedAt ? ` · ${relativeTime(n.publishedAt)}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {individualTrades.length === 0 && (
            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No individual trades found for this stock.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
        <DialogTitle>Mark as Assigned</DialogTitle>
        <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the quantity to mark as assigned. If less than the total quantity, the trade will be split.
            </Typography>
            <TextField
                autoFocus
                margin="dense"
                label="Quantity"
                type="number"
                fullWidth
                variant="outlined"
                value={assignQuantity}
                onChange={(e) => setAssignQuantity(e.target.value)}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitAssign} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockAnalysis;
